import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-wrap gutter-y-s3 flex-grow-1" }
const _hoisted_2 = { class: "w-12 sm:w-4" }
const _hoisted_3 = { class: "flex mr-s2 align-items-center" }
const _hoisted_4 = { class: "w-12 sm:w-4" }
const _hoisted_5 = { class: "mr-s2" }
const _hoisted_6 = { class: "w-12 sm:w-4" }
const _hoisted_7 = { class: "mr-s2" }
const _hoisted_8 = { class: "flex flex-wrap align-items-stretch gutter-s3 xl:flex-nowrap" }
const _hoisted_9 = { class: "w-12 xl:w-9 xl:flex-order-2" }
const _hoisted_10 = { class: "w-12 xl:w-3" }

import { computed, onMounted, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import brandedFarmsConfig from '@/helpers/brandedFarmsConfig.json';
import { Farm } from '@/sdk/entities/farm';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { FarmSourceType } from '@/sdk/entities/farmSource';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';
import { getScanLink } from '@/sdk/utils';
import { format, printUSD } from '@/utils/format';
import compareMixin from '@/mixins/compare.mixin';
import InfoMessage from '@/components/InfoMessage.vue';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { useTokens } from '@/store/modules/tokens/useTokens';
import Caption from '@/components/Caption.vue';
import Harvest from './harvest/Harvest.vue';
import Stake from './stake/Stake.vue';
import FarmTitle from './farmTokens/FarmTitle.vue';
import ControlPanelCollapse from '@/components/ControlPanelCollapse.vue';
import { useMilkomedaWSCBalanceADAIntoCardanoValidator } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBalanceADAIntoCardanoValidator';
import { TokenAmount } from '@/sdk/entities/fractions/tokenAmount';


export default /*#__PURE__*/_defineComponent({
  __name: 'Farm',
  props: {
    farm: null,
    openAfterCreated: { type: Boolean }
  },
  emits: ["sortFarms", "sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sortFarms'): void;
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  farm: Farm | PortfolioFarm;
  openAfterCreated: boolean;
}

const { compareAddresses } = compareMixin.methods;





const { t } = useI18n();
const { getTokenByAddressAndChainId } = useTokens();

onMounted(() => {
  console.log((props.farm as PortfolioFarm).portfolio?.type);
});

const balanceADAIntoCardanoValidator = useMilkomedaWSCBalanceADAIntoCardanoValidator();
const harvestBalanceADAIntoCardanoValidator = useMilkomedaWSCBalanceADAIntoCardanoValidator();

const isValidCardanoBalanceRef = toRef(
  balanceADAIntoCardanoValidator.milkomedaWSCBalanceADAIntoCardanoValidatorState,
  'isValidCardanoBalance',
);
const minADABalanceIntoCardanoRef = toRef(
  balanceADAIntoCardanoValidator.milkomedaWSCBalanceADAIntoCardanoValidatorState,
  'minADABalance',
);

const isShowNotEnoughCurrencyForFee = computed(
  () => ENABLE_FAKE_CARDANO_NETWORK && !isValidCardanoBalanceRef.value,
);
const brandedFarmInfo = computed(() => {
  return brandedFarmsConfig.find(brandedFarm =>
    compareAddresses(brandedFarm.farm, props.farm.farm),
  );
});
const isCrosschainFarm = computed(() => {
  return (props.farm as PortfolioFarm).portfolio.type === PortfolioSource.PORTFOLIO_CROSSCHAIN;
});
const poolToken = computed(() => {
  if (!DEFAULT_NETWORK_ID) return;
  return getTokenByAddressAndChainId(props.farm.tokenToMint, +DEFAULT_NETWORK_ID);
});
const earnedToken = computed(() => {
  if (!poolToken.value) return;
  if (!ENABLE_FAKE_CARDANO_NETWORK && poolToken.value.isWETHToken()) {
    return poolToken.value.unwrapWETH();
  }

  return poolToken.value;
});

function getApr() {
  const apr = props.farm.printIPR;
  if (apr < 1) {
    return `< 1%`;
  }
  return apr > 10000 ? `> 10 000%` : `${format(apr.toFixed(2))}%`;
}
function getLiquidity() {
  const liquidity = props.farm.printLiquidity;

  if (liquidity instanceof TokenAmount) {
    return printUSD(liquidity.toFixed());
  }

  return printUSD(liquidity);
}
function getEarned() {
  return format(props.farm.printEarned.toSignificant(6));
}
function isPortfolioFarm() {
  return props.farm.sourceType === FarmSourceType.PORTFOLIO;
}
function isPairFarm() {
  return props.farm.sourceType === FarmSourceType.PAIR;
}
function getPortfolioTokens() {
  const farm = props.farm as PortfolioFarm;
  const tokens = farm.portfolio.tokens.map(token => token.token);
  const crossChainTokens = farm.portfolio.crossChainTokens.map(token => token.token);
  return [...tokens, ...crossChainTokens];
}
function getFarmTokens() {
  return isPairFarm() ? (props.farm as Farm).pair : getPortfolioTokens();
}
function getContractUrl() {
  return getScanLink(props.farm.farm, 'address');
}
function onSendTransaction() {
  emits('sendTransaction');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ControlPanelCollapse, {
    "contract-url": getContractUrl(),
    name: isPortfolioFarm() ? (__props.farm as PortfolioFarm).portfolio.name : undefined,
    "toggle-inline": "",
    expanded: __props.openAfterCreated,
    brand: _unref(brandedFarmInfo)?.brand,
    "brand-icon": _unref(brandedFarmInfo)?.icon,
    crosschain: _unref(isCrosschainFarm)
  }, {
    "header-title": _withCtx(() => [
      _createVNode(FarmTitle, {
        tokens: getFarmTokens(),
        "source-type": __props.farm.sourceType
      }, null, 8, ["tokens", "source-type"])
    ]),
    "header-data": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(Caption, {
            caption: _unref(t)('farming.apr.caption'),
            tooltip: _unref(t)('farming.apr.tooltip'),
            class: "mb-s1"
          }, null, 8, ["caption", "tooltip"]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", null, _toDisplayString(getApr()), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(Caption, {
            caption: _unref(t)('farming.totalStake.caption'),
            tooltip: _unref(t)('farming.totalStake.tooltip'),
            class: "mb-s1"
          }, null, 8, ["caption", "tooltip"]),
          _createElementVNode("div", _hoisted_5, _toDisplayString(getLiquidity()), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(Caption, {
            caption: _unref(t)('farming.youEarned.caption'),
            tooltip: _unref(t)('farming.youEarned.tooltip'),
            class: "mb-s1"
          }, null, 8, ["caption", "tooltip"]),
          _createElementVNode("div", _hoisted_7, _toDisplayString(getEarned()) + " " + _toDisplayString(_unref(earnedToken)?.symbol), 1)
        ])
      ])
    ]),
    default: _withCtx(() => [
      (_unref(isShowNotEnoughCurrencyForFee))
        ? (_openBlock(), _createBlock(InfoMessage, {
            key: 0,
            type: "error",
            "as-panel": "",
            class: "mb-s3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.notEnoughCurrencyForFee', {
          amount: _unref(minADABalanceIntoCardanoRef),
          symbol: 'ADA',
        })), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createVNode(Stake, {
            class: "h-full",
            farm: (__props.farm as PortfolioFarm),
            tokens: getFarmTokens(),
            "wsc-ada-validator": _unref(balanceADAIntoCardanoValidator),
            onSortFarms: _cache[0] || (_cache[0] = ($event: any) => (emits('sortFarms'))),
            onSendTransaction: onSendTransaction
          }, null, 8, ["farm", "tokens", "wsc-ada-validator"])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(Harvest, {
            class: "h-full",
            tokens: getFarmTokens(),
            farm: (__props.farm as PortfolioFarm),
            "wsc-ada-validator": _unref(harvestBalanceADAIntoCardanoValidator),
            onSortFarms: _cache[1] || (_cache[1] = ($event: any) => (emits('sortFarms'))),
            onSendTransaction: onSendTransaction
          }, null, 8, ["tokens", "farm", "wsc-ada-validator"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["contract-url", "name", "expanded", "brand", "brand-icon", "crosschain"]))
}
}

})